import type { CSSProperties } from "react";
import type { Grade } from "../sendItEngine";
import type { MissionId } from "./content";
import { Ic } from "./icons";

function Badge({ ok }: { ok: boolean }) {
  return (
    <svg className={`sis-badge ${ok ? "is-ok" : "is-fix"}`} viewBox="0 0 52 52" width="46" height="46" aria-hidden="true">
      <circle className="sis-badge-c" cx="26" cy="26" r="23" />
      {ok ? <path className="sis-badge-p" d="m15.5 27.5 7.5 7.5 14-16" /> : <path className="sis-badge-p" d="M26 14.5v14M26 36.5v.6" />}
    </svg>
  );
}

/** What happened / Why it matters / What to do next, shown as a window on the desktop. */
export function ResultBody({
  grade,
  taskNo,
  total,
  missionId,
  last,
  whatIf,
  onWhatIf,
  onFix,
  onContinue,
  onSummary,
  offerMore,
  onMore,
  onDone,
}: {
  grade: Grade;
  taskNo: number;
  total: number;
  missionId: MissionId;
  last: boolean;
  whatIf: boolean;
  onWhatIf: () => void;
  onFix: () => void;
  onContinue: () => void;
  onSummary: () => void;
  offerMore?: boolean;
  onMore?: () => void;
  onDone?: () => void;
}) {
  const sec = (ms: number) => ({ "--d": `${ms}ms` }) as CSSProperties;
  return (
    <div className={`sis-res ${grade.ok ? "is-ok" : "is-fix"}`} role="status" aria-live="polite">
      <div className="sis-res-top">
        <Badge ok={grade.ok} />
        <div>
          <small>Task {taskNo} of {total}</small>
          <h3>{grade.ok ? "That works" : "Not quite yet"}</h3>
        </div>
      </div>
      <div className="sis-res-secs sis-scroll">
        <section style={sec(120)}>
          <h4>What happened</h4>
          <p>{grade.happened}</p>
        </section>
        <section style={sec(240)}>
          <h4>Why it matters</h4>
          <p>{grade.why}</p>
        </section>
        <section style={sec(360)}>
          <h4>What to do next</h4>
          <p>{grade.next}</p>
        </section>
        {whatIf && (
          <p className="sis-res-what">
            If the wrong person, the internal draft, or a public link had been used, someone outside the task could read or change the file. Your choice avoided that.
          </p>
        )}
        {missionId === "m6" && grade.ok && (
          <div className="sis-copy" role="note" style={sec(480)}>
            <span className="sis-copy-ic"><Ic n="download" size={18} /></span>
            <div>
              <span className="sis-tag">Simulated</span>
              <strong>Downloaded copy on Alex's computer</strong>
              <p>A file Alex downloaded earlier can still sit on their computer. Removing access here does not delete that copy, and this screen cannot see every real-world download.</p>
            </div>
          </div>
        )}
      </div>
      <footer className="sis-res-foot">
        {grade.ok && (
          <button type="button" className="sis-link" onClick={onWhatIf} aria-expanded={whatIf}>
            <Ic n="info" size={13} /> What could have happened?
          </button>
        )}
        <span className="sis-cmd-sp" />
        {!grade.ok && (
          <button type="button" className="sis-btn sis-btn--primary" onClick={onFix}>
            <Ic n="pencil" size={14} /> Fix it
          </button>
        )}
        {grade.ok && offerMore && (
          <>
            <button type="button" className="sis-btn" onClick={onDone}>
              Back to the terminal
            </button>
            <button type="button" className="sis-btn sis-btn--primary" onClick={onMore}>
              Do three more <Ic n="chevron" size={14} />
            </button>
          </>
        )}
        {grade.ok && !offerMore && !last && (
          <button type="button" className="sis-btn sis-btn--primary" onClick={onContinue}>
            Continue <Ic n="chevron" size={14} />
          </button>
        )}
        {grade.ok && !offerMore && last && (
          <button type="button" className="sis-btn sis-btn--primary" onClick={onSummary}>
            See the summary <Ic n="chevron" size={14} />
          </button>
        )}
      </footer>
    </div>
  );
}

export function SummaryBody({
  rows,
  onBack,
}: {
  rows: { id: string; title: string; first: boolean }[];
  onBack: () => void;
}) {
  return (
    <div className="sis-sum">
      <div className="sis-sum-top">
        <Badge ok />
        <div>
          <small>Workshop wrap-up</small>
          <h3>What you practised</h3>
        </div>
      </div>
      <ul className="sis-sum-list sis-scroll">
        {rows.map((r, i) => (
          <li key={r.id} style={{ "--i": i } as CSSProperties}>
            <span className="sis-sum-tick"><Ic n="check" size={13} /></span>
            <span className="sis-sum-tx">
              <strong>{r.title}</strong>
              <span>{r.first ? "Settled on the first try" : "Corrected before continuing"}</span>
            </span>
          </li>
        ))}
      </ul>
      <footer className="sis-res-foot">
        <span className="sis-cmd-sp" />
        <button type="button" className="sis-btn sis-btn--primary" onClick={onBack}>
          Back to the terminal <Ic n="chevron" size={14} />
        </button>
      </footer>
    </div>
  );
}
